import { ListProps, TextField, SelectField } from 'react-admin';

import {
  TextInput,
  ImageField,
  Grid,
  GridList,
  BooleanField,
  Forbidden,
  NullableBooleanInput,
  SelectInput, SelectArrayInput,
} from '@components';
import { useExporter, useRbacList } from '@hooks';
import { categoryOptions, teamCategoryOptions } from './category-options';
import { DoctypePosition } from './DoctypePosition';
import { requirementsOptions } from './requirements-options';
import { dealerTiersChoices } from '@types';

export const DoctypeList = (props: ListProps) => {
  const rbacList = useRbacList();
  const exporter = useExporter({
    filename: 'Documents',
    props: {
      'position': {
        label: 'Position',
      },
      'nameMx': {
        label: 'Title MX',
      },
      'infoMx': {
        label: 'Subtitle MX',
      },
      'nameEn': {
        label: 'Title EN',
      },
      'infoEn': {
        label: 'Subtitle EN',
      },
      'requirements': {
        label: 'Applies to type of User',
        transform: (requirements: string) => {
          return requirementsOptions.find(o => o.id === requirements)?.name || '';
        },
      },
      'excludedTiers': {
        label: 'Excluded for Tier',
      },
      'categoryName': {
        label: 'Сlient Category',
      },
      'teamCategory': {
        label: 'Team Category',
      },
      'isVisible': {
        label: 'Is visible for client/partner',
        transform: 'boolean',
      },
      'isDefault': {
        label: 'Default of Credit docs',
        transform: 'boolean',
      },
      'isGuarantor': {
        label: 'Default of Guarantor docs',
        transform: 'boolean',
      },
      'isOptional': {
        label: 'Is optional',
        transform: 'boolean',
      },
      'isSendToCredit': {
        label: 'Send to Analysis',
        transform: 'boolean',
      },
      'isRestricted': {
        label: 'Restricted',
        transform: 'boolean',
      },
      'isPrefilled': {
        label: 'Future Pre-fill',
        transform: 'boolean',
      },
    },
  });

  if (!rbacList) {
    return <Forbidden />;
  }

  return (
    <GridList {...props} filters={Filters} exporter={exporter}>
      <Grid
        rowClick="edit"
        defaultColumns={'position nameMx nameEn infoMx infoEn image requirements categoryName teamCategory isVisible isDefault isGuarantor isOptional'.split(' ')}
      >
        <TextField source="position" name="position" sortable={false} label="Position" />
        <DoctypePosition label="Move" sortable={false} />
        <TextField source="nameMx" label="Title MX" sortable={false} />
        <TextField source="infoMx" label="Subtitle MX" sortable={false} />
        <TextField source="nameEn" label="Title EN" sortable={false} />
        <TextField source="infoEn" label="Subtitle EN" sortable={false} />
        <ImageField source="image" label="Image" sortable={false} width={40} maxHeight={40} />
        <SelectField
          source="requirements"
          label="Applies to type of User"
          choices={requirementsOptions}
          sortable={false}
        />
        <TextField source="excludedTiers" label="Excluded for Tier" sortable={false} />
        <TextField source="categoryName" label="Сlient Category" sortable={false} />
        <TextField source="teamCategory" label="Team Category" sortable={false} />
        <BooleanField source="isVisible" label="Is visible for client/partner" />
        <BooleanField source="isDefault" label="Default of Credit docs" sortable={false} />
        <BooleanField source="isGuarantor" label="Default of Guarantor docs" sortable={false} />
        <BooleanField source="isOptional" label="Is optional" sortable={false} />
        <BooleanField source="isSendToCredit" label="Send to Analysis" />
        <BooleanField source="isRestricted" label="Restricted" />
        <BooleanField source="isPrefilled" label="Future Pre-fill" />
      </Grid>
    </GridList>
  );
};

const Filters = [
  <TextInput source="q" key="q" variant="outlined" label="Search" alwaysOn />,
  <NullableBooleanInput
    style={{ width: 200 }}
    source="isDefault"
    key="isDefault"
    label="Default of Credit docs"
    alwaysOn
    trueLabel="Yes"
    falseLabel="No"
  />,
  <SelectInput
    style={{ width: 250 }}
    source="requirements"
    key="requirements"
    label="Applies to user"
    choices={[
      { id: 'Individual', name: 'Individual' },
      { id: 'Company', name: 'Company' },
    ]}
    alwaysOn
  />,
  <NullableBooleanInput
    style={{ width: 220 }}
    source="isGuarantor"
    key="isGuarantor"
    label="Default of Guarantor docs"
    alwaysOn
    trueLabel="Yes"
    falseLabel="No"
  />,
  <NullableBooleanInput
    style={{ width: 220 }}
    source="isOptional"
    key="isOptional"
    label="Is optional"
    alwaysOn
    trueLabel="Yes"
    falseLabel="No"
  />,
  <SelectInput
    style={{ width: 250 }}
    source="category"
    key="category"
    label="Client Category"
    choices={categoryOptions}
    alwaysOn
  />,
  <SelectInput
    style={{ width: 250 }}
    source="teamCategory"
    key="teamCategory"
    label="Team Category"
    choices={teamCategoryOptions}
  />,
  <NullableBooleanInput
    style={{ width: 100 }}
    source="isVisible"
    key="isVisible"
    label="Is visible for client/partner"
    trueLabel="Yes"
    falseLabel="No"
  />,
  <SelectArrayInput
    style={{ width: 100 }}
    key="excludedTiers"
    source="excludedTiers"
    label="Excluded for Tier"
    choices={dealerTiersChoices}
    className={'MS-field'}
  />,
  <NullableBooleanInput
    style={{ width: 100 }}
    source="isRestricted"
    key="isRestricted"
    label="Restricted"
    trueLabel="Yes"
    falseLabel="No"
  />,
  <NullableBooleanInput
    style={{ width: 100 }}
    source="isPrefilled"
    key="isPrefilled"
    label="Future Pre-fill"
    trueLabel="Yes"
    falseLabel="No"
  />,
];
